// Shared constants for Iterable mailing-category labels + grouping.
// Keeps ETKP Development (scenes/etkp-development) and the Iterable Campaigns
// tab (scenes/iterable-statistics) in lockstep — the 10 category keys here
// must match MAILING_CATEGORIES in node-app/src/helpers/mailingClassification.ts.

export const MAILING_CATEGORIES = [
  'sam',
  'non_opener_vnr',
  'non_opener_plx',
  'non_opener_webinar',
  'newsletter',
  'heatup',
  'redlink',
  'plx_mailing',
  'followup',
  'heatup_legacy',
  'other',
] as const;

export type MailingCategory = (typeof MAILING_CATEGORIES)[number];

// `Record<string, string>` so callers can pass a plain `string` category
// coming from the API without needing to narrow to MailingCategory.
export const CATEGORY_LABELS: Record<string, string> = {
  sam: 'VNR SAM',
  non_opener_vnr: 'Non-Opener (VNR)',
  non_opener_plx: 'Non-Opener (PLX)',
  non_opener_webinar: 'Non-Opener (Webinar)',
  newsletter: 'Newsletter',
  heatup: 'Webinar (Heat-Up)',
  redlink: 'Webinar (Redlink)',
  plx_mailing: 'PLX Direct Mailing',
  followup: 'Webinar Followup',
  heatup_legacy: 'Webinar Heat-Up (Legacy/Draft)',
  other: 'Sonstige',
};

// MUI Chip color per category. Keeps the detail modal + chip groups visually
// consistent across pages.
export const CATEGORY_CHIP_COLOR: Record<
  string,
  'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning'
> = {
  sam: 'success',
  non_opener_vnr: 'warning',
  non_opener_plx: 'warning',
  non_opener_webinar: 'warning',
  newsletter: 'info',
  heatup: 'primary',
  redlink: 'primary',
  plx_mailing: 'secondary',
  followup: 'default',
  heatup_legacy: 'default',
  other: 'default',
};

// Categories whose revenue is systematically attributable. Must match
// REVENUE_ATTRIBUTED_CATEGORIES in the backend helper. `newsletter` revenue
// is resolved live by NewsletterRevenueResolver (4-source attribution) and
// JS-merged post-aggregation in the /etkp-development endpoint.
export const REVENUE_ATTRIBUTED_CATEGORIES: readonly MailingCategory[] = [
  'sam',
  'non_opener_vnr',
  'heatup',
  'redlink',
  'plx_mailing',
  'non_opener_plx',
  'non_opener_webinar',
  'newsletter',
];

export function hasRevenueAttribution(category: string): boolean {
  return (REVENUE_ATTRIBUTED_CATEGORIES as readonly string[]).includes(category);
}

// Newsletter slot zones — must mirror NEWSLETTER_ZONES in
// node-app/src/services/NewsletterRevenueResolver.ts. Drives the ETKP
// development zone filter and the per-row breakdown in the detail modal.
export const NEWSLETTER_ZONES = [
  'ki_ad',
  'checkpoint',
  'bottom',
  'ps',
  'html_box',
  'unknown',
] as const;
export type NewsletterZone = (typeof NEWSLETTER_ZONES)[number];

// `bottom` is the internal key (matches NewsletterRevenueResolver.ts), but the
// label is "Native" because the FT NL bottom slot has always been a native ad
// position. The internal key kept stable so URL filters / saved state stay
// compatible.
export const ZONE_LABELS: Record<string, string> = {
  ki_ad: 'KI Ad (inline)',
  checkpoint: 'Checkpoint',
  bottom: 'Native',
  ps: 'PS',
  html_box: 'HTML unten',
  unknown: 'Sonstige',
};

export type ZoneRevenue = Record<NewsletterZone, number>;

export function isNewsletterZone(value: string): value is NewsletterZone {
  return (NEWSLETTER_ZONES as readonly string[]).includes(value);
}
